import { useThree } from '@react-three/fiber';
import * as React from 'react';
import * as THREE from 'three/webgpu';
import hljs from 'highlight.js';

function useTslDebugValue() {
  const [vertexShader, setVertexShader] = React.useState<string | null>(null);
  const [fragmentShader, setFragmentShader] = React.useState<string | null>(null);
  return {
    vertexShader,
    fragmentShader,
    setVertexShader,
    setFragmentShader,
  };
}

export type TslDebugContext = ReturnType<typeof useTslDebugValue>;

const TslDebugContext = React.createContext<TslDebugContext | null>(null);

export function TslDebugProvider(props: { children: React.ReactNode }) {
  const { children } = props;
  const value = useTslDebugValue();
  return <TslDebugContext.Provider value={value}>{children}</TslDebugContext.Provider>;
}

export type Props = {
  meshName: string;
};

export function useTslDebugContext() {
  const context = React.useContext(TslDebugContext);
  if (context == null) {
    throw new Error('useTslDebugContext must be used within a TslDebugProvider');
  }
  return context;
}

export function TslDebug(props: Props) {
  const { meshName } = props;
  const { scene, camera, gl } = useThree();
  const { setVertexShader, setFragmentShader } = useTslDebugContext();
  React.useEffect(() => {
    const mesh = scene.getObjectByName(meshName) as THREE.Mesh;
    if (mesh == null) {
      console.warn('TslDebug: mesh not found', meshName);
      return;
    }
    (gl as unknown as THREE.WebGPURenderer).debug.getShaderAsync(scene, camera, mesh).then((shader) => {
      setVertexShader(shader.vertexShader);
      setFragmentShader(shader.fragmentShader);
    });
  }, []);

  console.log('TslDebug');
  return <></>;
}

export declare namespace TslDebugViewer {
  export type Props = {
    showVertexShader?: boolean;
    showFragmentShader?: boolean;
  };
}

const DEFAULT_VIEWER_HEIGHT = '300px';

export function TslDebugViewer({ showVertexShader, showFragmentShader }: TslDebugViewer.Props) {
  const { vertexShader, fragmentShader } = useTslDebugContext();
  React.useEffect(() => {
    // console.log('initHighlighting');
    hljs.highlightAll();
  }, [vertexShader, fragmentShader]);
  const gridTemplateColumns = React.useMemo(() => {
    const ret = [];
    if (showVertexShader) {
      ret.push('1fr');
    }
    if (showFragmentShader) {
      ret.push('1fr');
    }
    return ret.join(' ');
  }, [showVertexShader, showFragmentShader]);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns,
      }}
    >
      {showVertexShader && (
        <div>
          <h6 style={{ margin: 0 }}>Vertex Shader WGSL 생성 코드</h6>
          <pre style={{ whiteSpace: 'pre-wrap', overflow: 'auto', height: DEFAULT_VIEWER_HEIGHT }}>
            <code className="hljs language-glsl">{vertexShader}</code>
          </pre>
        </div>
      )}
      {showFragmentShader && (
        <div>
          <h6 style={{ margin: 0 }}>Fragment Shader WGSL생성 코드</h6>
          <pre style={{ whiteSpace: 'pre-wrap', overflow: 'auto', height: DEFAULT_VIEWER_HEIGHT }}>
            <code className="hljs language-glsl">{fragmentShader}</code>
          </pre>
        </div>
      )}
    </div>
  );
}
